import { SizeFilter } from 'src/types/graphql'

/**
 * Largest storage entry in the guide is 3×9 = 27 m² (Grande). Beyond ~30 m²
 * the reference photo + size category lose representational value — the
 * listing is in commercial warehouse / industrial nave territory and the
 * "fits 30 boxes" framing reads as misleading. Consumers (`SizeSection`)
 * use this to gate whether to render the section at all.
 */
export const MAX_SIZE_GUIDE_AREA_SQM = 30

export type SizeGuideEntry = {
  id: string
  dimensions: { width: number; length: number }
  sizeFilter: SizeFilter
  /** i18n key prefix under SIZES, e.g. 'LOCKER' */
  i18nKey: string
}

export type SizeGuideCategory = {
  id: string
  /** i18n key under TABS, e.g. 'SMALL' */
  i18nKey: string
  sizes: SizeGuideEntry[]
}

/** Keep measurements identical in selectors, captions and comparison tables. */
export const formatSizeNumber = (value: number): string =>
  String(Number(value.toFixed(2)))

export const formatSizeDimensions = ({
  width,
  length
}: SizeGuideEntry['dimensions']): string =>
  `${formatSizeNumber(width)} × ${formatSizeNumber(length)} m`

export const getSizeArea = ({ width, length }: SizeGuideEntry['dimensions']) =>
  width * length

/** Only an example for the comparison table, never a listing's actual height. */
export const SIZE_GUIDE_EXAMPLE_HEIGHT_M = 2.5

export const SIZE_GUIDE_CATEGORIES: SizeGuideCategory[] = [
  {
    id: 'small',
    i18nKey: 'SMALL',
    sizes: [
      {
        id: 's1.5x1.5',
        dimensions: { width: 1.5, length: 1.5 },
        sizeFilter: SizeFilter.Xsmall,
        i18nKey: 'LOCKER'
      },
      {
        id: 's1.5x1.5c',
        dimensions: { width: 1.5, length: 1.5 },
        sizeFilter: SizeFilter.Xsmall,
        i18nKey: 'CLOSET'
      },
      {
        id: 's1.5x3',
        dimensions: { width: 1.5, length: 3 },
        sizeFilter: SizeFilter.Small,
        i18nKey: 'STUDIO'
      }
    ]
  },
  {
    id: 'medium',
    i18nKey: 'MEDIUM',
    sizes: [
      {
        id: 's1.5x4.5',
        dimensions: { width: 1.5, length: 4.5 },
        sizeFilter: SizeFilter.Small,
        i18nKey: 'WALK_IN'
      },
      {
        id: 's3x3',
        dimensions: { width: 3, length: 3 },
        sizeFilter: SizeFilter.Medium,
        i18nKey: 'ONE_BED'
      },
      {
        id: 's3x4.5',
        dimensions: { width: 3, length: 4.5 },
        sizeFilter: SizeFilter.Medium,
        i18nKey: 'TWO_BED'
      }
    ]
  },
  {
    id: 'large',
    i18nKey: 'LARGE',
    sizes: [
      {
        id: 's3x6',
        dimensions: { width: 3, length: 6 },
        sizeFilter: SizeFilter.Large,
        i18nKey: 'THREE_BED'
      },
      {
        id: 's3x7.5',
        dimensions: { width: 3, length: 7.5 },
        sizeFilter: SizeFilter.Large,
        i18nKey: 'HOUSE'
      },
      {
        id: 's3x9',
        dimensions: { width: 3, length: 9 },
        sizeFilter: SizeFilter.Large,
        i18nKey: 'LARGE_HOUSE'
      }
    ]
  },
  {
    id: 'vehicle',
    i18nKey: 'VEHICLE',
    sizes: [
      {
        id: 'v2x4',
        dimensions: { width: 2, length: 4 },
        sizeFilter: SizeFilter.Parking,
        i18nKey: 'MOTORCYCLE'
      },
      {
        id: 'v3x5',
        dimensions: { width: 3, length: 5 },
        sizeFilter: SizeFilter.Parking,
        i18nKey: 'CAR'
      },
      {
        id: 'v3x6',
        dimensions: { width: 3, length: 6 },
        sizeFilter: SizeFilter.Parking,
        i18nKey: 'STANDARD_VEHICLE'
      },
      {
        id: 'v3.5x7',
        dimensions: { width: 3.5, length: 7 },
        sizeFilter: SizeFilter.Parking,
        i18nKey: 'SUV'
      }
    ]
  },
  {
    id: 'fleet',
    i18nKey: 'FLEET',
    sizes: [
      {
        id: 'f3x5',
        dimensions: { width: 3, length: 5 },
        sizeFilter: SizeFilter.Parking,
        i18nKey: 'CARGO_VAN'
      },
      {
        id: 'f3x6',
        dimensions: { width: 3, length: 6 },
        sizeFilter: SizeFilter.Parking,
        i18nKey: 'BOX_TRUCK'
      },
      {
        id: 'f3.5x9',
        dimensions: { width: 3.5, length: 9 },
        sizeFilter: SizeFilter.Parking,
        i18nKey: 'BUS'
      },
      {
        id: 'f3.5x16',
        dimensions: { width: 3.5, length: 16 },
        sizeFilter: SizeFilter.Parking,
        i18nKey: 'SEMI_TRUCK'
      }
    ]
  }
]

export const SIZE_IMAGE_MAP: Record<string, string> = {
  LOCKER: '/images/sizes/small/small-locker-v2.webp',
  CLOSET: '/images/sizes/small/small-closet-v2.webp',
  STUDIO: '/images/sizes/small/small-studio-v2.webp',
  WALK_IN: '/images/sizes/medium/medium-closet-v2.webp',
  ONE_BED: '/images/sizes/medium/medium-1-bedroom-v2.webp',
  TWO_BED: '/images/sizes/medium/medium-2-bedroom-v2.webp',
  THREE_BED: '/images/sizes/large/large-3-bedroom-v2.webp',
  HOUSE: '/images/sizes/large/large-4-bedroom-v2.webp',
  LARGE_HOUSE: '/images/sizes/large/large-full-house-v2.webp',
  MOTORCYCLE: '/images/sizes/vehicle/moto-v2.webp',
  CAR: '/images/sizes/vehicle/auto-compact-v2.webp',
  STANDARD_VEHICLE: '/images/sizes/vehicle/auto-standard-v2.webp',
  SUV: '/images/sizes/vehicle/suv-v2.webp',
  CARGO_VAN: '/images/sizes/flotilla/van-v2.webp',
  BOX_TRUCK: '/images/sizes/flotilla/camion-v2.webp',
  BUS: '/images/sizes/flotilla/bus-v2.webp',
  SEMI_TRUCK: '/images/sizes/flotilla/trailer-v2.webp'
}

/**
 * Find the best-matching tab + size entry for a given listing.
 * Returns { categoryId, sizeId } to pre-select the modal.
 */
export function findClosestSize(
  area: number,
  isVehicle: boolean
): { categoryId: string; sizeId: string } {
  // Pick the relevant categories
  const candidateCategories = isVehicle
    ? SIZE_GUIDE_CATEGORIES.filter(
        (c) => c.id === 'vehicle' || c.id === 'fleet'
      )
    : SIZE_GUIDE_CATEGORIES.filter(
        (c) => c.id === 'small' || c.id === 'medium' || c.id === 'large'
      )

  let bestCat = candidateCategories[0]
  let bestSize = bestCat.sizes[0]
  let bestDiff = Infinity

  for (const cat of candidateCategories) {
    for (const size of cat.sizes) {
      const sizeArea = size.dimensions.width * size.dimensions.length
      const diff = Math.abs(sizeArea - area)
      if (diff < bestDiff) {
        bestDiff = diff
        bestCat = cat
        bestSize = size
      }
    }
  }

  return { categoryId: bestCat.id, sizeId: bestSize.id }
}
